// studio/modules/freshcards/src/hostCallbacks.ts
//
// IntegrationBridge host callbacks, re-imported from FreshCards standalone
// b7e4717 (src/shell/IntegrationBridge.ts). Slimmed to the 3 host
// callbacks the surface model needs: onNavigate, onSurfaceChange,
// onCommandExecute.
//
// Per operator 2026-07-14 15:49 UTC, piece 11 of 11.

/** Host callbacks for surface navigation. */
export interface HostNavigatePayload {
  surface: string;
  params?: Record<string, unknown>;
}

export interface HostSurfaceChangePayload {
  surface: string;
  params?: Record<string, unknown>;
}

export interface HostCommandPayload {
  command: string;
  params?: Record<string, unknown>;
}

export interface HostCallbacks {
  onNavigate?: (payload: HostNavigatePayload) => void;
  onSurfaceChange?: (payload: HostSurfaceChangePayload) => void;
  onCommandExecute?: (payload: HostCommandPayload) => void;
}

/** Default no-op host callbacks. */
export const noopHostCallbacks: Required<HostCallbacks> = {
  onNavigate: () => {},
  onSurfaceChange: () => {},
  onCommandExecute: () => {},
};

/** Compose host callbacks (later wins). */
export function composeHostCallbacks(
  ...callbacks: HostCallbacks[]
): Required<HostCallbacks> {
  return callbacks.reduce<Required<HostCallbacks>>(
    (acc, cb) => ({
      onNavigate: cb.onNavigate ?? acc.onNavigate,
      onSurfaceChange: cb.onSurfaceChange ?? acc.onSurfaceChange,
      onCommandExecute: cb.onCommandExecute ?? acc.onCommandExecute,
    }),
    noopHostCallbacks
  );
}

/**
 * WorkspaceDef slot. Wire these on the FreshCards WorkspaceDef to integrate
 * with the chrome's host integration system. When the chrome detects a
 * workspace switch, it calls these to inform the workspace of state changes.
 *
 * The chrome (StudioContext) is the host. The workspace (FreshCards) is
 * the guest. This file is the boundary contract.
 */
export type FreshCardsHostCallbacks = HostCallbacks;
